import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MetalRateStrip from '../components/MetalRateStrip';
import ProductCard from '../components/ProductCard';
import OrderPipeline from '../components/OrderPipeline';
import OnboardingModal from '../components/OnboardingModal';
import SEOHead from '../components/SEOHead';
import './BuyerPage.css';

const BuyerPage = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalRole, setAuthModalRole] = useState('buyer');

  const openAuth = (role) => {
    setAuthModalRole(role);
    setAuthModalOpen(true);
  };

  const workflowSteps = [
    {
      step: 'Discover',
      icon: '🔍',
      headline: 'Multi-Seller Hallmarked Catalogues',
      desc: 'Browse live inventory across 7 major jewellery segments. Filter by exact karatage (18K, 22K, 24K, 925), gross weight range, and karigar region.',
    },
    {
      step: 'Select',
      icon: '📐',
      headline: 'Product Intelligence & Spec Audit',
      desc: 'Inspect exact weight deductions, net precious metal weight, certified gemstone carats, and clear making charge benchmarks.',
    },
    {
      step: 'Order',
      icon: '📝',
      headline: 'Structured B2B Cart',
      desc: 'Group wholesale orders by design line, sizes, and batch tolerances. Generate instant wholesale RFQs or instant purchase orders.',
    },
    {
      step: 'Confirm',
      icon: '🤝',
      headline: 'Spot Rate Lock & Allocation',
      desc: 'Seller accepts order, locks pure bullion rate against live India benchmarks, and earmarks raw materials or finished inventory.',
    },
    {
      step: 'Pay',
      icon: '💳',
      headline: 'Flexible Escrow & Settlement',
      desc: 'Select payment preference according to agreed terms: RTGS advance, secured bank escrow, or verified commercial credit lines.',
    },
    {
      step: 'Receive',
      icon: '📦',
      headline: 'Counter Receipt & Weight Verification',
      desc: 'Receive tamper-sealed consignment via insured transit with barcoded tare certification, digital weighing slip, and HUID verification.',
    },
  ];

  const buyerFeatures = [
    {
      icon: '🛡️',
      title: 'Verified Sellers Only',
      desc: 'Every manufacturer undergoes GSTIN authentication, physical atelier verification, and BIS hallmarking certification checks.',
    },
    {
      icon: '📑',
      title: 'Structured Wholesale Orders',
      desc: 'Orders organized systematically by SKU, size variants, gross weight lots, and making charge tiers rather than unstructured chat orders.',
    },
    {
      icon: '⚖️',
      title: 'Transparent Specifications',
      desc: 'Uncompromising precision: gross weight, net precious metal, enamel/stone deductions, purity hallmarking, and certificate serials.',
    },
    {
      icon: '💳',
      title: 'Flexible Settlement Preferences',
      desc: 'Select bank transfer, advance token, escrow release upon assay verification, or approved commercial credit schedules.',
    },
    {
      icon: '🚚',
      title: '8-Stage Custody Order Tracking',
      desc: 'Track every shipment in real time from seller preparation, assay sorting, vault staging, to verified counter receipt.',
    },
    {
      icon: '🗄️',
      title: 'Digital Tax Invoices & Records',
      desc: 'Keep complete purchase history, GST e-way bills, HUID audit slips, and digital payment receipts systematically organized.',
    },
  ];

  return (
    <>
      <SEOHead 
        title="Buy Wholesale Jewellery - B2B Jewellery Buyers Platform"
        description="Join JMI as a verified wholesale jewellery buyer. Access BIS hallmarked gold, diamond, silver, and platinum jewellery directly from manufacturers with live rates."
        url="https://jewelmartindia.com/buyer"
        keywords="wholesale jewellery buyer, B2B jewellery purchase, gold jewellery wholesale, diamond jewellery buyers, silver jewellery bulk order"
      />
      <div className="app-wrapper buyer-page-view">
      <MetalRateStrip />
      <Navbar onOpenAuthModal={openAuth} />

      <main>
        {/* Buyer Hero */}
        <section className="buyer-hero-section">
          <div className="jmi-container buyer-hero-grid">
            <div className="buyer-hero-copy">
              <div className="section-label">BUYER SOURCING PLATFORM</div>
              <h1 className="buyer-hero-title">
                Your Wholesale Jewellery <span className="gold-gradient-text">Sourcing Platform</span>
              </h1>
              <p className="buyer-hero-sub">
                Find jewellery from verified sellers, create orders with precise product specifications 
                and manage the entire purchase journey from one place.
              </p>

              <div className="buyer-cta-group">
                <a 
                  href="https://play.google.com/store/apps/details?id=com.jmi.jmi" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-primary btn-lg"
                >
                  <span>Download Buyer App</span>
                  <span className="btn-arrow">↓</span>
                </a>
                <button 
                  className="btn btn-secondary btn-lg"
                  onClick={() => openAuth('buyer')}
                >
                  Register as Buyer
                </button>
              </div>

              <div className="buyer-quick-stats">
                <div className="stat-pill">
                  <strong>₹0</strong> Buyer Commission
                </div>
                <div className="stat-pill">
                  <strong>BIS HUID</strong> Mandatory
                </div>
                <div className="stat-pill">
                  <strong>100%</strong> Insured Transit
                </div>
              </div>
            </div>

            {/* Product Intelligence Card Showcase */}
            <div className="buyer-hero-card-col">
              <ProductCard onAddToOrder={() => openAuth('buyer')} />
            </div>
          </div>
        </section>

        {/* Buyer Workflow Timeline */}
        <section className="buyer-workflow-section">
          <div className="jmi-container">
            <div className="section-header-center">
              <div className="section-label">STEP-BY-STEP SOURCING</div>
              <h2 className="section-title">
                The Buyer <span className="gold-gradient-text">Procurement Journey</span>
              </h2>
              <p className="section-subtitle">
                A transparent, governed six-stage workflow designed specifically for commercial retail jewellers and showroom owners.
              </p>
            </div>

            <div className="timeline-flow-track">
              {workflowSteps.map((wf, idx) => (
                <div key={idx} className="timeline-step-card">
                  <div className="step-badge-number">Stage 0{idx + 1}</div>
                  <div className="step-icon-bubble">{wf.icon}</div>
                  <h3 className="step-action-name">{wf.step}</h3>
                  <h4 className="step-subhead">{wf.headline}</h4>
                  <p className="step-detail-p">{wf.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Order Tracking Pipeline Highlight */}
        <section className="buyer-pipeline-section">
          <div className="jmi-container">
            <div className="section-header-center">
              <div className="section-label">CUSTODY TRANSPARENCY</div>
              <h2 className="section-title">
                Live <span className="gold-gradient-text">Order Tracking</span>
              </h2>
              <p className="section-subtitle">
                Never wonder where your high-value stock is. Track orders across all 8 verified transit and preparation stages.
              </p>
            </div>

            <OrderPipeline />
          </div>
        </section>

        {/* Buyer Features Grid */}
        <section className="buyer-features-section">
          <div className="jmi-container">
            <div className="section-header-center">
              <div className="section-label">BUYER ADVANTAGES</div>
              <h2 className="section-title">
                Engineered for <span className="gold-gradient-text">Professional Procurement</span>
              </h2>
              <p className="section-subtitle">
                Replace fragmented WhatsApp messages and unverified brokers with institutional B2B procurement software.
              </p>
            </div>

            <div className="buyer-features-grid">
              {buyerFeatures.map((feat, idx) => (
                <div key={idx} className="buyer-feat-card">
                  <div className="feat-icon">{feat.icon}</div>
                  <h3 className="feat-title">{feat.title}</h3>
                  <p className="feat-desc">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Buyer CTA Banner */}
        <section className="buyer-banner-cta-section">
          <div className="jmi-container">
            <div className="buyer-cta-banner-box">
              <h2>Empower Your Jewellery Showroom with JMI</h2>
              <p>
                Access verified national manufacturing hubs in Mumbai, Surat, Rajkot, Kolkata, and Coimbatore at transparent B2B rates.
              </p>
              <div className="banner-btn-row">
                <button 
                  className="btn btn-primary btn-lg"
                  onClick={() => openAuth('buyer')}
                >
                  Register as Verified Buyer →
                </button>
                <a 
                  href="https://play.google.com/store" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-secondary btn-lg"
                >
                  Download Buyer App
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer onOpenAuthModal={openAuth} />
      <OnboardingModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        initialRole={authModalRole} 
      />
    </div>
    </>
  );
};

export default BuyerPage;
